import type { Group } from '@cheqroom/api';
import { useMemo } from 'react';

import { useLimits } from '../limits/use-limits';

/** The workspace fields the shared packages read (Info components, opening hours). */
export type LegacyWorkspace = Pick<
	Group,
	'profile' | 'limits' | 'itemFlags' | 'itemTypes' | 'businessHours' | 'ticketTypes'
>;

const NO_TICKET_TYPES: LegacyWorkspace['ticketTypes'] = [];

/**
 * The workspace in the python-api `Group` shape, built from the legacy Knockout globals — the one place
 * web adapts them for `@cheqroom/components` (`InfoProvider`) and `@cheqroom/hooks` (`useOpeningHours`).
 *
 * Legacy group hours are ISO weekdays (core `Group._fromBusinessHoursJson` rewrites the API's 0-based
 * `dayOfWeek`); they are mapped back here. The globals are not React state: values are read at render, so
 * a change without a re-render is missed, but any re-render picks up the latest.
 */
export const useLegacyWorkspace = (): LegacyWorkspace => {
	const limits = useLimits();
	const profile = window.global.central.profile();
	const { businessHours, itemFlags, itemTypes, ticketTypes } = window.global.central.group;

	return useMemo(
		() =>
			({
				limits,
				profile,
				itemFlags,
				itemTypes,
				// Raw group payload (core `Group` keeps `ticketTypes` as the API sends them).
				ticketTypes: ticketTypes ?? NO_TICKET_TYPES,
				businessHours: (businessHours ?? []).map(({ isoWeekday, openTime, closeTime }) => ({
					dayOfWeek: isoWeekday - 1,
					openTime,
					closeTime,
				})),
				// The legacy global types (`Partial<Limits>`, `Record<string, unknown>` profile) predate the
				// python-api `Group` types; the data is the same group payload.
			}) as unknown as LegacyWorkspace,
		[limits, profile, itemFlags, itemTypes, ticketTypes, businessHours]
	);
};
